/**
 * Help Panel Content
 * Per-page help content in English and Georgian.
 */

export interface HelpSection {
  heading: string;
  items: string[];
}

export interface HelpPageContent {
  title: string;
  intro: string;
  sections: HelpSection[];
}

export type HelpContentMap = Record<string, HelpPageContent>;

const helpContent: Record<'en' | 'ka', HelpContentMap> = {
  en: {
    general: {
      title: 'AIIA Platform',
      intro:
        'AIIA lets you build AI-powered chat assistants and embed them anywhere — your website, WhatsApp, Telegram, Messenger, Viber, or Instagram. Configure your AI persona, connect your APIs as tools, and attach a knowledge base.',
      sections: [
        {
          heading: 'Quick Start',
          items: [
            'Create an API Key (API Keys page)',
            'Upload documents to use as knowledge base (Data Files page)',
            'Define your AI persona (Prompts page)',
            'Create a Widget and assign a Prompt + Tools',
            'Copy the embed script from Widget Editor → Embed tab',
          ],
        },
        {
          heading: 'Key Concepts',
          items: [
            'Widget — the chat interface embedded on your site or channel',
            'Prompt — AI personality, instructions, model settings',
            'Tool — your external API the AI can call during chat',
            'Data File — document uploaded for RAG knowledge retrieval',
            'Agent Mode — multi-step tool chaining (ReAct loop)',
          ],
        },
      ],
    },

    dashboard: {
      title: 'Dashboard',
      intro:
        'An overview of your account activity — conversations, messages, sessions, and token usage across all your widgets.',
      sections: [
        {
          heading: 'Metrics',
          items: [
            'Total Conversations — all chat sessions ever started',
            'Active Sessions — conversations in progress right now',
            'Messages Today — messages exchanged in the last 24 hours',
            'Token Usage — LLM tokens consumed this billing period',
          ],
        },
        {
          heading: 'Getting Started',
          items: [
            'Create an API Key to authenticate /chat API calls',
            'Create a Widget and embed it on your website',
            'Assign a Prompt to define your AI persona',
            'Add Tools to let the AI call your external APIs',
          ],
        },
      ],
    },

    analytics: {
      title: 'Analytics',
      intro:
        'Detailed conversation and usage metrics over time. Track volumes, session lengths, and token consumption per widget.',
      sections: [
        {
          heading: 'Available Reports',
          items: [
            'Conversation timeline — daily chat volume chart',
            'Top widgets — which widgets receive the most traffic',
            'Session duration — average conversation length',
            'Token breakdown — usage per model and widget',
            'Channel distribution — web vs WhatsApp vs Telegram etc.',
          ],
        },
        {
          heading: 'Tips',
          items: [
            'Use date range filters to compare different periods',
            'High token usage on short conversations may indicate prompt inefficiency',
            'A drop in sessions may indicate a widget embed issue',
          ],
        },
      ],
    },

    apiKeys: {
      title: 'API Keys',
      intro:
        'API keys authenticate your server-side calls to the /chat endpoint. Each key is bound to your account. Create separate keys per environment to isolate access.',
      sections: [
        {
          heading: 'How to Use',
          items: [
            'POST /api/v1/chat with header: X-Api-Key: <your-key>',
            'Include widgetId in the body to route to a specific widget',
            'Include sessionId to maintain conversation continuity',
            'The API returns response text + conversationId + sessionId',
          ],
        },
        {
          heading: 'Best Practices',
          items: [
            'Never expose keys in client-side / browser code',
            'Create separate keys for dev, staging, and production',
            'Revoke a key immediately if it is compromised',
            'Rotate keys periodically',
            'Monitor last-used timestamps for unused key detection',
          ],
        },
      ],
    },

    widgets: {
      title: 'Widgets',
      intro:
        'A Widget is an embeddable AI chat interface. Each widget has its own prompt, tools, and branding. You can create multiple widgets — one per product, department, or use case.',
      sections: [
        {
          heading: 'Features',
          items: [
            'Multiple widgets per account with independent configurations',
            'Custom branding — colors, fonts, position on page',
            'Prompt assignment — define AI personality per widget',
            'Tool selection — control which APIs the AI can call',
            'Knowledge base — attach data files for RAG retrieval',
            'Channels — WhatsApp, Telegram, Messenger, Viber, Instagram',
          ],
        },
        {
          heading: 'Setup Steps',
          items: [
            '1. Create a Widget (give it a name)',
            '2. Assign a Prompt (AI personality)',
            '3. Add Tools (optional — lets AI call your APIs)',
            '4. Attach Data Files (optional — knowledge base)',
            '5. Open Widget Editor → Embed tab → copy the <script> tag',
            '6. Paste the script in your website HTML',
          ],
        },
        {
          heading: 'Channels',
          items: [
            'WhatsApp — connect via Meta Business API webhook',
            'Telegram — connect via Bot token',
            'Facebook Messenger — connect via Page access token',
            'Viber — connect via Viber Bot token',
            'Instagram — connect via Meta webhook',
          ],
        },
      ],
    },

    widgetEditor: {
      title: 'Widget Editor',
      intro:
        'Configure every aspect of your widget — appearance, AI behavior, knowledge base, tools, and channel integrations.',
      sections: [
        {
          heading: 'Editor Tabs',
          items: [
            'Basic — name, description, active/inactive toggle',
            'Theme — primary color, text color, border radius, font',
            'Header — chat window title, subtitle, avatar image',
            'Layout — position (bottom-right / bottom-left / fullscreen)',
            'Messages — welcome message, input placeholder, typing indicator',
            'AI & RAG — assign Prompt, attach data files for retrieval',
            'Tools — select which tools this widget can access',
            'Channels — WhatsApp / Telegram / Messenger / Viber / Instagram',
            'Embed — copy the <script> tag for your website',
          ],
        },
        {
          heading: 'AI & RAG Tab',
          items: [
            'Select a Prompt to define AI personality and model settings',
            'Attach Data Files — the AI will search them for relevant context',
            'RAG settings (Top K, similarity threshold) are on the Prompt level',
          ],
        },
        {
          heading: 'Embed Tab',
          items: [
            'Copy the <script> tag and paste it before </body> in your HTML',
            'The script loads the widget asynchronously — no impact on page speed',
            'widgetId is embedded in the script — do not share the URL publicly',
          ],
        },
      ],
    },

    dataFiles: {
      title: 'Data Files',
      intro:
        'Upload documents to build a knowledge base. Files are processed (chunked + embedded) and stored in a vector database. The AI retrieves relevant chunks before answering — this is called RAG (Retrieval-Augmented Generation).',
      sections: [
        {
          heading: 'Supported Formats',
          items: ['PDF (.pdf)', 'Plain Text (.txt)', 'Word Document (.docx)', 'Markdown (.md)'],
        },
        {
          heading: 'Processing Pipeline',
          items: [
            'PENDING — file uploaded, waiting for processing',
            'PROCESSING — text extraction, chunking, embedding',
            'READY — indexed in vector store, available for RAG',
            'FAILED — processing error (try re-uploading)',
          ],
        },
        {
          heading: 'Usage',
          items: [
            'After status is READY, attach the file to a Prompt (Prompts page)',
            'You can also attach files directly from the Widget Editor → AI & RAG tab',
            'The AI searches all attached files and injects the top-K relevant chunks',
            'Adjust Top K and Similarity Threshold in Prompt → RAG Config',
          ],
        },
        {
          heading: 'Tips',
          items: [
            'Focused, well-structured documents give better retrieval accuracy',
            'Split large documents into topic-specific files',
            'Use clear headings — they improve chunk relevance scoring',
          ],
        },
      ],
    },

    tools: {
      title: 'Tools',
      intro:
        'Tools let the AI call your external APIs during a conversation. Define the URL, parameters, and response mapping — the AI decides when and how to call the tool based on the conversation context.',
      sections: [
        {
          heading: 'Tool Types',
          items: [
            'Webhook — HTTP call to any external URL (your own API, third-party)',
            'Internal — calls to built-in platform endpoints',
          ],
        },
        {
          heading: 'Configuration Fields',
          items: [
            'URL — supports {path_variable} substitution (e.g. /orders/{order_id})',
            'Method — GET, POST, PUT, PATCH, DELETE',
            'Parameters — define the schema the LLM must fill in',
            'Required params — fields the LLM must always provide',
            'Secrets — encrypted API tokens injected at runtime',
            'Request template — transform LLM args into the exact request body',
            'Response mapping — extract specific fields from the API response',
            'Success / Error message templates — human-readable result format',
          ],
        },
        {
          heading: 'Tips',
          items: [
            'Write a precise Description — the LLM reads it to decide when to use the tool',
            'Use {path_variable} in the URL for resource ID parameters',
            'Enable Agent Mode on the Prompt for multi-step tool chains',
            'Test tools independently before attaching them to a widget',
          ],
        },
      ],
    },

    prompts: {
      title: 'Prompts',
      intro:
        'A Prompt defines your AI assistant\'s personality, behavior, and model settings. One prompt can be shared across multiple widgets. Think of it as the "brain" configuration.',
      sections: [
        {
          heading: 'Core Settings',
          items: [
            'Name — identifier used when assigning to widgets',
            'System Prompt — instructions the AI follows (persona, rules, tone)',
            'Model — gemini-2.5-flash (fast, recommended), gemini-2.5-pro, GPT-4o',
            'Temperature — 0.0 = precise/deterministic, 2.0 = creative/varied',
            'Max Tokens — maximum length of AI responses',
          ],
        },
        {
          heading: 'RAG Configuration',
          items: [
            'Enable RAG to make the AI search attached knowledge base files',
            'Top K — how many document chunks to retrieve per query (1-20)',
            'Similarity Threshold — minimum relevance score (0.0-1.0, default 0.5)',
            'Higher threshold = fewer but more relevant results',
          ],
        },
        {
          heading: 'Agent Mode',
          items: [
            'Enable for multi-step tasks that require calling several tools in sequence',
            'The AI reasons about each tool result before deciding the next action',
            'Max Iterations — limits the number of tool calls per turn (1-20, default 5)',
            'Write your system prompt to guide the AI on which tools to use in what order',
            'agentSteps are returned in the API response for observability',
          ],
        },
        {
          heading: 'Knowledge Base',
          items: [
            'Attach uploaded Data Files to give the AI domain-specific knowledge',
            'Files can also be attached per-widget via Widget Editor → AI & RAG',
            'Widget-level files are merged with prompt-level files at query time',
          ],
        },
      ],
    },

    settings: {
      title: 'Settings',
      intro: 'Manage your profile, preferences, and account security.',
      sections: [
        {
          heading: 'Profile',
          items: [
            'Display name — shown in the portal header',
            'Email — your login email (change via Keycloak account page)',
            'Language — switch the portal interface between English and Georgian',
          ],
        },
        {
          heading: 'Security',
          items: [
            'Password — managed via your Keycloak account',
            'Two-factor authentication — configure in Keycloak account settings',
            'Active sessions — view and revoke other active logins',
          ],
        },
        {
          heading: 'Account',
          items: [
            'View your account ID (useful for support requests)',
            'Delete account — permanently removes all data (irreversible)',
          ],
        },
      ],
    },

    subscription: {
      title: 'Subscription',
      intro:
        'Manage your plan, billing cycle, usage limits, and invoice history.',
      sections: [
        {
          heading: 'Plans',
          items: [
            'Free — limited monthly messages, 1 widget, community support',
            'Pro — higher message limits, multiple widgets, priority support',
            'Enterprise — unlimited, dedicated support, SLA, custom models',
          ],
        },
        {
          heading: 'Usage Limits',
          items: [
            'Messages per month — resets on billing anniversary date',
            'Widgets — maximum number of active widgets',
            'Data files — storage quota for knowledge base documents',
            'API calls — rate limit per minute per API key',
          ],
        },
        {
          heading: 'Billing',
          items: [
            'Upgrade or downgrade at any time — prorated billing',
            'Download invoices from the invoice history table',
            'Cancel before next billing cycle to avoid charges',
            'Payment methods: card via Stripe',
          ],
        },
      ],
    },
  },

  // ===== GEORGIAN =====
  ka: {
    general: {
      title: 'AIIA პლატფორმა',
      intro:
        'AIIA საშუალებას გაძლევს შექმნა AI ჩეთ-ასისტენტი და ჩასვა ნებისმიერ ადგილას — საიტზე, WhatsApp-ზე, Telegram-ზე, Messenger-ზე, Viber-ზე ან Instagram-ზე. განსაზღვრე AI-ს პიროვნება, დაუკავშირე შენი API-ები ინსტრუმენტებად და დაამატე ცოდნის ბაზა.',
      sections: [
        {
          heading: 'სწრაფი დაწყება',
          items: [
            'შექმენი API Key (API Keys გვერდი)',
            'ატვირთე დოკუმენტები ცოდნის ბაზისთვის (Data Files გვერდი)',
            'განსაზღვრე AI-ს პიროვნება (Prompts გვერდი)',
            'შექმენი Widget, მიაბი Prompt + Tools',
            'დააკოპირე embed კოდი Widget Editor → Embed ჩანართიდან',
          ],
        },
        {
          heading: 'ძირითადი ცნებები',
          items: [
            'Widget — ჩეთ-ინტერფეისი საიტზე ან არხზე',
            'Prompt — AI-ს პიროვნება, ინსტრუქციები, მოდელის პარამეტრები',
            'Tool — შენი გარე API, რომელსაც AI ჩეთის დროს იძახებს',
            'Data File — ატვირთული დოკუმენტი RAG ძიებისთვის',
            'Agent Mode — მრავალსაფეხურიანი ინსტრუმენტების ჯაჭვი',
          ],
        },
      ],
    },

    dashboard: {
      title: 'დაფა',
      intro:
        'შენი ანგარიშის აქტივობის მიმოხილვა — საუბრები, შეტყობინებები, სესიები და ტოკენების მოხმარება ყველა ვიჯეტზე.',
      sections: [
        {
          heading: 'მეტრიკა',
          items: [
            'სულ საუბრები — ოდესმე დაწყებული ყველა ჩეთ-სესია',
            'აქტიური სესიები — ახლა მიმდინარე საუბრები',
            'შეტყობინებები დღეს — ბოლო 24 საათის შეტყობინებები',
            'ტოკენების მოხმარება — ამ სააბონენტო პერიოდში გამოყენებული LLM ტოკენები',
          ],
        },
        {
          heading: 'დასაწყებად',
          items: [
            'შექმენი API Key /chat API გამოძახებების ავტენტიფიკაციისთვის',
            'შექმენი Widget და ჩასვი საიტზე',
            'მიაბი Prompt AI-ს პიროვნების განსასაზღვრად',
            'დაამატე Tools AI-ს გარე API-ებთან დასაკავშირებლად',
          ],
        },
      ],
    },

    analytics: {
      title: 'ანალიტიკა',
      intro:
        'დეტალური მეტრიკა საუბრებისა და გამოყენების შესახებ. თვალი ადევნე მოცულობებს, სესიების ხანგრძლივობასა და ტოკენების მოხმარებას ვიჯეტების მიხედვით.',
      sections: [
        {
          heading: 'ხელმისაწვდომი ანგარიშები',
          items: [
            'საუბრების გრაფიკი — ყოველდღიური ჩეთ-მოცულობა',
            'ტოპ ვიჯეტები — ყველაზე პოპულარული ვიჯეტები',
            'სესიის ხანგრძლივობა — საშუალო საუბრის სიგრძე',
            'ტოკენების განაწილება — მოხმარება მოდელისა და ვიჯეტის მიხედვით',
            'არხების განაწილება — web, WhatsApp, Telegram და სხვ.',
          ],
        },
        {
          heading: 'რჩევები',
          items: [
            'გამოიყენე თარიღის ფილტრი სხვადასხვა პერიოდის შედარებისთვის',
            'ტოკენების მაღალი მოხმარება მოკლე საუბრებში შეიძლება მიუთითებდეს prompt-ის არაეფექტურობაზე',
            'სესიების შემცირება შეიძლება მიუთითებდეს embed კოდის პრობლემაზე',
          ],
        },
      ],
    },

    apiKeys: {
      title: 'API გასაღებები',
      intro:
        'API გასაღებები ამოწმებს შენი სერვერის გამოძახებებს /chat endpoint-ზე. თითოეული გასაღები დაკავშირებულია შენს ანგარიშთან. შექმენი ცალ-ცალკე გასაღებები სხვადასხვა გარემოსთვის.',
      sections: [
        {
          heading: 'გამოყენება',
          items: [
            'POST /api/v1/chat ჰედერით: X-Api-Key: <შენი-გასაღები>',
            'body-ში შეიყვანე widgetId კონკრეტულ ვიჯეტზე მისამართვად',
            'sessionId-ის გამოყენება საუბრის გაგრძელებისთვის',
            'API აბრუნებს: response, conversationId, sessionId',
          ],
        },
        {
          heading: 'უსაფრთხოება',
          items: [
            'არასდროს გამოიტანო გასაღები კლიენტის (browser) კოდში',
            'შექმენი ცალ-ცალკე გასაღებები dev, staging და production-ისთვის',
            'კომპრომეტირებული გასაღები დაუყოვნებლივ გაუქმდი',
            'პერიოდულად განაახლე გასაღებები',
            'თვალი ადევნე "ბოლო გამოყენების" თარიღს გამოუყენებელი გასაღებების გამოსავლენად',
          ],
        },
      ],
    },

    widgets: {
      title: 'ვიჯეტები',
      intro:
        'ვიჯეტი — ჩასართავი AI ჩეთ-ინტერფეისი. თითოეულ ვიჯეტს აქვს საკუთარი Prompt, ინსტრუმენტები და ბრენდინგი. შეგიძლია შექმნა რამდენიმე ვიჯეტი — სხვადასხვა პროდუქტისთვის, განყოფილებისთვის ან საიტის გვერდისთვის.',
      sections: [
        {
          heading: 'ფიჩერები',
          items: [
            'მრავალი ვიჯეტი ანგარიშზე, დამოუკიდებელი კონფიგურაციებით',
            'ბრენდინგი — ფერები, შრიფტი, პოზიცია გვერდზე',
            'Prompt — განსაზღვრე AI-ს პიროვნება ვიჯეტისთვის',
            'Tools — განსაზღვრე რომელ API-ებს გამოიყენებს AI',
            'ცოდნის ბაზა — დაამატე Data Files RAG-ისთვის',
            'არხები — WhatsApp, Telegram, Messenger, Viber, Instagram',
          ],
        },
        {
          heading: 'დაყენების ნაბიჯები',
          items: [
            '1. შექმენი Widget (მიეცი სახელი)',
            '2. მიაბი Prompt (AI-ს პიროვნება)',
            '3. დაამატე Tools (სურვილისამებრ — API გამოძახებებისთვის)',
            '4. მიაბი Data Files (სურვილისამებრ — ცოდნის ბაზა)',
            '5. Widget Editor → Embed ჩანართი → დააკოპირე <script> კოდი',
            '6. ჩასვი კოდი საიტის HTML-ში',
          ],
        },
        {
          heading: 'არხები',
          items: [
            'WhatsApp — Meta Business API webhook-ის გამოყენებით',
            'Telegram — Bot Token-ის გამოყენებით',
            'Facebook Messenger — Page Access Token-ის გამოყენებით',
            'Viber — Viber Bot Token-ის გამოყენებით',
            'Instagram — Meta webhook-ის გამოყენებით',
          ],
        },
      ],
    },

    widgetEditor: {
      title: 'ვიჯეტის რედაქტორი',
      intro:
        'კონფიგურაცია გარეგნობა, AI-ს ქცევა, ცოდნის ბაზა, ინსტრუმენტები და არხები — ყველაფერი ერთ ადგილას.',
      sections: [
        {
          heading: 'ჩანართები',
          items: [
            'ძირითადი — სახელი, აღწერა, აქტიური/არააქტიური',
            'თემა — ძირითადი ფერი, ტექსტის ფერი, border radius, შრიფტი',
            'სათაური — ჩეთ-ფანჯრის სათაური, ქვესათაური, ავატარი',
            'განლაგება — პოზიცია (მარჯვნივ ქვემოთ / მარცხნივ ქვემოთ / სრული ეკრანი)',
            'შეტყობინებები — მისასალმებელი, placeholder, typing ინდიკატორი',
            'AI & RAG — Prompt-ის მიბმა, Data Files-ის დამატება',
            'Tools — ამ ვიჯეტისთვის ხელმისაწვდომი ინსტრუმენტები',
            'არხები — WhatsApp / Telegram / Messenger / Viber / Instagram',
            'Embed — <script> კოდი საიტისთვის',
          ],
        },
        {
          heading: 'AI & RAG ჩანართი',
          items: [
            'Prompt-ის არჩევა AI-ს პიროვნებისა და მოდელის პარამეტრების განსასაზღვრად',
            'Data Files-ის დამატება — AI მოძებნის მათში რელევანტურ კონტექსტს',
            'RAG პარამეტრები (Top K, similarity) Prompt-ის დონეზე იმართება',
          ],
        },
        {
          heading: 'Embed ჩანართი',
          items: [
            'დააკოპირე <script> კოდი და ჩასვი </body>-მდე HTML-ში',
            'სკრიპტი ასინქრონულად იტვირთება — გვერდის სიჩქარეზე გავლენა არ აქვს',
            'widgetId ჩართულია სკრიპტში — ნუ გაუზიარებ URL-ს საჯაროდ',
          ],
        },
      ],
    },

    dataFiles: {
      title: 'მონაცემთა ფაილები',
      intro:
        'ატვირთე დოკუმენტები ცოდნის ბაზის შესაქმნელად. ფაილები მუშავდება (იყოფა ნაწილებად + ჩაიდება ვექტორებში) და ინახება pgvector ბაზაში. AI მოძებნის რელევანტურ ნაწილებს შეკითხვამდე — ამას ეწოდება RAG.',
      sections: [
        {
          heading: 'მხარდაჭერილი ფორმატები',
          items: ['PDF (.pdf)', 'ტექსტური ფაილი (.txt)', 'Word დოკუმენტი (.docx)', 'Markdown (.md)'],
        },
        {
          heading: 'დამუშავების ეტაპები',
          items: [
            'PENDING — ატვირთული, ელოდება დამუშავებას',
            'PROCESSING — ტექსტის ამოღება, დაყოფა, ვექტორიზაცია',
            'READY — ინდექსირებულია, RAG-ისთვის მზადაა',
            'FAILED — შეცდომა (სცადე ხელახლა ატვირთვა)',
          ],
        },
        {
          heading: 'გამოყენება',
          items: [
            'READY სტატუსის შემდეგ მიაბი ფაილი Prompt-ს (Prompts გვერდი)',
            'შეგიძლია ფაილი მიაბი ვიჯეტსაც Widget Editor → AI & RAG ჩანართიდან',
            'AI ეძებს ყველა მიბმულ ფაილში და ინექტირებს ყველაზე რელევანტურ ნაწილებს',
            'Top K და Similarity Threshold-ი Prompt → RAG Config-ში რეგულირდება',
          ],
        },
        {
          heading: 'რჩევები',
          items: [
            'ფოკუსირებული, კარგად სტრუქტურირებული დოკუმენტები უკეთ პოულობენ',
            'დიდი დოკუმენტები დაყავი თემატური ფაილებად',
            'გამოყენე მკაფიო სათაურები — ისინი აუმჯობესებს retrieval-ის ზუსტობას',
          ],
        },
      ],
    },

    tools: {
      title: 'ინსტრუმენტები',
      intro:
        'ინსტრუმენტები საშუალებას აძლევენ AI-ს, ჩეთის დროს გამოიძახოს შენი გარე API-ები. განსაზღვრე URL, პარამეტრები და response mapping — AI გადაწყვეტს როდის და რა სახელს გამოიყენებს.',
      sections: [
        {
          heading: 'ინსტრუმენტის ტიპები',
          items: [
            'Webhook — HTTP გამოძახება ნებისმიერ გარე URL-ზე (შენი API, მესამე მხარე)',
            'Internal — პლატფორმის ჩაშენებული endpoint-ები',
          ],
        },
        {
          heading: 'კონფიგურაციის ველები',
          items: [
            'URL — მხარდაჭერს {path_variable} ჩანაცვლებას (მაგ. /orders/{order_id})',
            'Method — GET, POST, PUT, PATCH, DELETE',
            'Parameters — სქემა, რომელიც LLM-ს უნდა შეავსოს',
            'Required params — სავალდებულო ველები',
            'Secrets — დაშიფრული API ტოკენები (runtime-ზე ინექტირდება)',
            'Request template — LLM-ის args-ების ტრანსფორმაცია სწორ request body-ში',
            'Response mapping — API პასუხიდან კონკრეტული ველების ამოღება',
            'Success/Error შეტყობინების შაბლონები — ადამიანისთვის გასაგები ფორმატი',
          ],
        },
        {
          heading: 'რჩევები',
          items: [
            'დაწერე ზუსტი Description — LLM კითხულობს მას გადაწყვეტის მისაღებად',
            'გამოიყენე {path_variable} URL-ში რესურსის ID-ების პარამეტრებისთვის',
            'Prompt-ზე Agent Mode-ის ჩართვა საჭიროა მრავალსაფეხურიანი ჯაჭვებისთვის',
            'სანამ ვიჯეტს მიაბი, ინსტრუმენტი ცალ-ცალკე გამოსცადე',
          ],
        },
      ],
    },

    prompts: {
      title: 'Prompt-ები',
      intro:
        'Prompt განსაზღვრავს AI ასისტენტის პიროვნებას, ქცევასა და მოდელის პარამეტრებს. ერთი Prompt შეიძლება გაუზიარო რამდენიმე ვიჯეტს. ეს არის AI-ს "ტვინის" კონფიგურაცია.',
      sections: [
        {
          heading: 'ძირითადი პარამეტრები',
          items: [
            'სახელი — identifier ვიჯეტებზე მიბმისთვის',
            'System Prompt — ინსტრუქციები AI-სთვის (პიროვნება, წესები, ტონი)',
            'მოდელი — gemini-2.5-flash (სწრაფი, რეკომენდებული), gemini-2.5-pro, GPT-4o',
            'ტემპერატურა — 0.0 = ზუსტი/განსაზღვრული, 2.0 = კრეატიული/მრავალფეროვანი',
            'Max Tokens — AI-ს პასუხის მაქსიმალური სიგრძე',
          ],
        },
        {
          heading: 'RAG კონფიგურაცია',
          items: [
            'ჩართე RAG რათა AI ეძებდეს მიბმულ ცოდნის ბაზის ფაილებში',
            'Top K — რამდენი chunk ამოიღოს ერთ მოთხოვნაზე (1-20)',
            'Similarity Threshold — მინიმალური რელევანტურობის ქულა (0.0-1.0, default 0.5)',
            'მაღალი threshold = ნაკლები, მაგრამ უფრო რელევანტური შედეგები',
          ],
        },
        {
          heading: 'Agent Mode (ახალი!)',
          items: [
            'ჩართე მრავალსაფეხურიანი ამოცანებისთვის, სადაც საჭიროა რამდენიმე ინსტრუმენტის თანმიმდევრულად გამოყენება',
            'AI მსჯელობს თითოეული ინსტრუმენტის შედეგზე შემდეგი ნაბიჯის გადაწყვეტამდე',
            'Max Iterations — ინსტრუმენტის გამოძახებების ლიმიტი ერთ შეტყობინებაზე (1-20, default 5)',
            'System Prompt-ში ახსენი AI-ს რომელი ინსტრუმენტი რა თანმიმდევრობით გამოიყენოს',
            'agentSteps API პასუხში ბრუნდება — ხედავ ყველა ნაბიჯს',
          ],
        },
        {
          heading: 'ცოდნის ბაზა',
          items: [
            'მიაბი ატვირთული Data Files AI-ს სფეროში ცოდნის მისაცემად',
            'ფაილები ასევე შეიძლება მიაბი ვიჯეტ-დონეზე Widget Editor → AI & RAG-დან',
            'ვიჯეტ-დონის ფაილები Prompt-ის ფაილებთან ერთად გაერთიანდება query-ს დროს',
          ],
        },
      ],
    },

    settings: {
      title: 'პარამეტრები',
      intro: 'მართე პროფილი, პრეფერენციები და ანგარიშის უსაფრთხოება.',
      sections: [
        {
          heading: 'პროფილი',
          items: [
            'სახელი — ნაჩვენებია პორტალის სათაურში',
            'ელ-ფოსტა — login ელ-ფოსტა (იცვლება Keycloak-ის ანგარიშის გვერდზე)',
            'ენა — გადართე ინტერფეისი ქართულსა და ინგლისურს შორის',
          ],
        },
        {
          heading: 'უსაფრთხოება',
          items: [
            'პაროლი — მართდება Keycloak-ის ანგარიშიდან',
            'ორფაქტორიანი ავთენტიფიკაცია — Keycloak-ის პარამეტრებში',
            'აქტიური სესიები — ნახე და გააუქმე სხვა შესვლები',
          ],
        },
        {
          heading: 'ანგარიში',
          items: [
            'ნახე ანგარიშის ID (სასარგებლოა support-ის შეკითხვებისთვის)',
            'ანგარიშის წაშლა — ყველა მონაცემი სამუდამოდ წაიშლება (შეუქცევადია)',
          ],
        },
      ],
    },

    subscription: {
      title: 'გამოწერა',
      intro: 'მართე პაკეტი, ბილინგის ციკლი, გამოყენების ლიმიტები და ინვოისების ისტორია.',
      sections: [
        {
          heading: 'პაკეტები',
          items: [
            'Free — ლიმიტირებული შეტყობინებები/თვეში, 1 ვიჯეტი, კომუნიტარული support',
            'Pro — მაღალი ლიმიტები, მრავალი ვიჯეტი, პრიორიტეტული support',
            'Enterprise — შეუზღუდავი, დედიკირებული support, SLA, ინდივიდუალური მოდელები',
          ],
        },
        {
          heading: 'გამოყენების ლიმიტები',
          items: [
            'შეტყობინებები/თვეში — განახლდება ბილინგის თარიღზე',
            'ვიჯეტები — მაქსიმალური აქტიური ვიჯეტების რაოდენობა',
            'Data Files — ცოდნის ბაზის ფაილების ლიმიტი',
            'API გამოძახებები — rate limit წუთში, API Key-ის მიხედვით',
          ],
        },
        {
          heading: 'ბილინგი',
          items: [
            'Upgrade/Downgrade ნებისმიერ დროს — პრო-რატა ბილინგი',
            'ინვოისების ჩამოტვირთვა ინვოისების ცხრილიდან',
            'გააუქმე შემდეგი ბილინგის ციკლამდე — დამატებითი გადახდა არ მოხდება',
            'გადახდა: ბარათით Stripe-ის გამოყენებით',
          ],
        },
      ],
    },
  },
};

export default helpContent;

export function getPageKey(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  // Remove language prefix (en, ka)
  const withoutLang = segments.filter(s => s !== 'en' && s !== 'ka');

  if (withoutLang.length === 0) return 'general';

  const first = withoutLang[0];
  const second = withoutLang[1];

  // Widget editor routes
  if (first === 'widgets' && (second === 'new' || second)) {
    if (second === 'new' || withoutLang.length > 1) return 'widgetEditor';
    return 'widgets';
  }

  const keyMap: Record<string, string> = {
    dashboard: 'dashboard',
    analytics: 'analytics',
    'api-keys': 'apiKeys',
    widgets: 'widgets',
    'data-files': 'dataFiles',
    tools: 'tools',
    prompts: 'prompts',
    settings: 'settings',
    subscription: 'subscription',
  };

  return keyMap[first] ?? 'general';
}
